/**
 * result-v2/adapters/providers.ts
 * ---------------------------------------------------------------------------
 * The piece that is missing from `prepareCommonResult`: grouping
 * `apiDataEntities[]` by provider.
 *
 * WHY THIS EXISTS
 * A single Phone Lookup in your data returns **25 entities for 14 declared
 * providers** — getcontact, viber, tc, whatsapp, telegram, viewcaller, botim,
 * eyecon, hiya and callapp each appear twice. The second pass is usually empty
 * or `FAILED_AGENT_INFO`. The old renderer walked the array as-is, which is
 * exactly why the result popup showed Zephyr, WhatsApp, Cosmos, Viewcaller and
 * Tc twice.
 *
 * `groupProviders()` collapses them to one row per provider, keeps the richest
 * non-failed response, and reports how many were merged so the UI can say so
 * instead of hiding it.
 */

/** Keys present on every entity payload that are envelope, not data. */
const ENVELOPE = new Set([
  'type', 'error', 'local_time', 'query', 'request', 'failed', 'misconfigured',
  'isLast', 'isMultiPhoneRequest', 'from', 'status', 'userId', 'requestId',
  'criteria', 'requestMonitorId',
])

export type ProviderStatus = 'data' | 'empty' | 'failed'

export type ProviderGroup = {
  /** provider key, e.g. 'tc', 'luna', 'getcontact' */
  key: string
  status: ProviderStatus
  /** number of raw entities collapsed into this row (1 = no duplicates) */
  merged: number
  /** flattened key/value pairs of the winning payload, for the detail panel */
  fields: [string, string][]
  /** the winning raw payload, kept for Raw view and debugging */
  raw: any
  /** short note explaining what was dropped, if anything */
  note?: string
}

export type ProviderSummary = {
  groups: ProviderGroup[]
  withData: ProviderGroup[]
  emptyCount: number
  failedCount: number
  duplicatesDropped: number
  /** providers declared on the request vs providers that actually answered */
  declared: number
  answered: number
}

const isEmptyValue = (v: any): boolean =>
  v === null ||
  v === undefined ||
  v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)

/**
 * Getcontact returns `email: "undefined,                null,               null,"`.
 * `service.ts` already tries to clean this, but it matches the literal strings
 * `"undefined, "` and `"null, "` — the padded whitespace slips through, which
 * is what produces the `,null,null,null` you saw on screen.
 */
export const cleanScalar = (v: any): string => {
  if (typeof v !== 'string') return String(v)
  const cleaned = v
    .replace(/\b(undefined|null)\b/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/(^[\s,]+)|([\s,]+$)/g, '')
    .replace(/,\s*,/g, ',')
    .trim()
  return cleaned
}

/** True when a value is only made of undefined/null noise. */
export const isJunk = (v: any): boolean => typeof v === 'string' && cleanScalar(v) === '' && v.trim() !== ''

/** "contact.phones" -> "Phones", "isUser" -> "Is user" — readable row labels. */
const humanise = (path: string): string => {
  const last = path.split('.').pop() || path
  return last
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

/** Keys that are transport noise rather than data. */
const NOISE = /^(cacheTtl|requestId|id|guid|index|indexDB|isLast|local_time|score_?raw)$/i

const flatten = (obj: any, prefix = '', out: [string, string][] = [], depth = 0): [string, string][] => {
  if (depth > 2 || obj === null || typeof obj !== 'object') return out
  Object.entries(obj).forEach(([k, v]) => {
    if (ENVELOPE.has(k) || NOISE.test(k)) return
    const path = prefix ? `${prefix}.${k}` : k
    if (v === null || v === undefined) return

    if (Array.isArray(v)) {
      if (!v.length) return
      // arrays of scalars read better inline than as "3 items"
      const scalars = v.filter((x) => x === null || typeof x !== 'object')
      if (scalars.length === v.length) {
        out.push([humanise(path), v.map((x) => cleanScalar(x)).filter(Boolean).join(', ')])
      } else if (v.length === 1 && typeof v[0] === 'object') {
        flatten(v[0], path, out, depth + 1)
      } else {
        out.push([humanise(path), `${v.length} records`])
        flatten(v[0], path, out, depth + 1)
      }
      return
    }

    if (typeof v === 'object') {
      flatten(v, path, out, depth + 1)
      return
    }

    const str = cleanScalar(v)
    if (str === '' || str === 'false' && /^(failed|misconfigured)$/i.test(k)) return
    out.push([humanise(path), str])
  })
  return out
}

/**
 * Some providers are wired up with the full response-payload key as
 * `entity.from` instead of the short name every other provider uses (e.g.
 * `entity.from === 'emailSearchResultGlook'`, not `'glook'`) — see
 * store/searcher/service.ts's own `entity.from === 'glook'` special case,
 * which only fires once `from` has already been shortened and so never
 * actually matches this shape. Left alone, the badge and the coverage-matrix
 * key both read "emailsearchresultglook" / "emailsearchresultsmf" — correct,
 * but not what the workflow catalogue (or the analyst) calls them.
 */
const KEY_ALIASES: Record<string, string> = {
  emailsearchresultglook: 'glook',
  emailsearchresultsmf: 'smf',
  emailsearchresulthbp: 'hbp',
}
const aliasKey = (k: string): string => KEY_ALIASES[k] ?? k

/** The provider payload inside an entity: entity.data[entity.from], or the single non-envelope key. */
const payloadOf = (entity: any): { key: string; payload: any } | null => {
  const data = entity?.data
  if (!data) return null
  if (Array.isArray(data)) {
    const key = aliasKey(String(entity.from || 'result').toLowerCase())
    return { key, payload: data }
  }
  const from = entity.from && data[entity.from] !== undefined ? entity.from : null
  if (from) return { key: aliasKey(String(from).toLowerCase()), payload: data[from] }
  const own = Object.keys(data).filter((k) => !ENVELOPE.has(k))
  if (!own.length) return { key: aliasKey(String(entity.from || 'unknown').toLowerCase()), payload: null }
  // Prefer a key that actually carries something — some providers (e.g.
  // reverse_phone: `{ methodName: null, reversePhone: [...] }`) leave an
  // empty envelope-ish field ahead of the real payload key in insertion
  // order, and blindly taking `own[0]` picked `methodName: null` instead of
  // the array that holds the actual result.
  const meaningfulKey = own.find((k) => !isEmptyValue(data[k])) ?? own[0]
  return { key: aliasKey(meaningfulKey.toLowerCase()), payload: data[meaningfulKey] }
}

const statusOf = (entity: any, payload: any): ProviderStatus => {
  const d = entity?.data
  const failedFlag = d && (d.failed === true || d.type === 'FAILED_AGENT_INFO' || (payload && payload.failed === true && isEmptyValue(payload.name)))
  if (failedFlag) return 'failed'
  if (isEmptyValue(payload)) return 'empty'
  // an object whose every value is empty counts as empty too
  if (typeof payload === 'object' && !Array.isArray(payload)) {
    const meaningful = Object.entries(payload).filter(([k, v]) => !ENVELOPE.has(k) && !isEmptyValue(v))
    if (!meaningful.length) return 'empty'
  }
  return 'data'
}

const richness = (g: ProviderGroup) => (g.status === 'data' ? 1000 : g.status === 'empty' ? 10 : 0) + g.fields.length

/**
 * Collapse apiDataEntities into one row per provider.
 * @param row a SearchResult straight from the store
 */
export function groupProviders(row: any): ProviderSummary {
  const entities: any[] = Array.isArray(row?.apiDataEntities) ? row.apiDataEntities : []
  const declaredSources: any[] = Array.isArray(row?.sources) ? row.sources : []

  const byKey = new Map<string, ProviderGroup>()
  let duplicatesDropped = 0

  entities.forEach((entity) => {
    const p = payloadOf(entity)
    if (!p) return
    const status = statusOf(entity, p.payload)
    const candidate: ProviderGroup = {
      key: p.key,
      status,
      merged: 1,
      fields: Array.isArray(p.payload) ? [['items', String(p.payload.length)]] : flatten(p.payload),
      raw: p.payload,
    }

    const existing = byKey.get(p.key)
    if (!existing) {
      byKey.set(p.key, candidate)
      return
    }

    duplicatesDropped += 1
    const merged = existing.merged + 1
    const winner = richness(candidate) > richness(existing) ? candidate : existing
    const loser = winner === candidate ? existing : candidate
    winner.merged = merged
    winner.note =
      loser.status === 'failed'
        ? `A second ${p.key} response failed and was dropped.`
        : loser.status === 'empty'
        ? `A second ${p.key} response was empty and was dropped.`
        : `${merged} ${p.key} responses were returned; the richest one is shown.`
    byKey.set(p.key, winner)
  })

  const groups = Array.from(byKey.values()).sort((a, b) => richness(b) - richness(a))

  // `sources[]` does not always list every provider that replies — a phone
  // lookup declares 14 but also returns PLUTO and VERIFICATION entities. Take
  // the larger of the two so the headline can never read "15 of 14".
  const declared = Math.max(declaredSources.length, groups.length)

  return {
    groups,
    withData: groups.filter((g) => g.status === 'data'),
    emptyCount: groups.filter((g) => g.status === 'empty').length,
    failedCount: groups.filter((g) => g.status === 'failed').length,
    duplicatesDropped,
    declared,
    answered: groups.length,
  }
}

/** Convenience: `12 of 14 providers returned data`. */
export const providerHeadline = (s: ProviderSummary) =>
  `${s.withData.length} of ${s.declared} provider${s.declared === 1 ? '' : 's'} returned data`
